// Contexto para las pestañas del legajo de empleados
import React, { createContext, useContext, useState, useEffect } from "react";
import {
  fetchTypesDoc,
  fetchProvincias,
  fetchSectores,
  fetchPuestos,
  fetchSucursales,
  fetchEstadoCivil,
  fetchPaises,
  fetchSindicatos,
  fetchConvenios,
  fetchBancos,
  fetchTelefonosTipos,
} from "../features/legajos/services/empleados.api";
import { v4 as uuidv4 } from "uuid";
// Custom hook for fetching reference data
const useLegajoReferenceData = () => {
  const [referenceData, setReferenceData] = useState({
    tiposDoc: [],
    provincias: [],
    sectores: [],
    puestos: [],
    sucursales: [],
    estadosCiviles: [],
    paises: [],
    sindicatos: [],
    bancos: [],
    telefonosTipos: [],
    isLoaded: false,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const results = await Promise.allSettled([
          fetchTypesDoc(),
          fetchProvincias(),
          fetchSectores(),
          fetchPuestos(),
          fetchSucursales(),
          fetchEstadoCivil(),
          fetchPaises(),
          fetchSindicatos(),
          fetchBancos(),
          fetchTelefonosTipos(),

        ]);

        const getValue = (result) => (result.status === "fulfilled" ? result.value : []);

        const [
          tiposDocRes,
          provinciasRes,
          sectoresRes,
          puestosRes,
          sucursalesRes,
          estadosCivilesRes,
          paisesRes,
          sindicatosRes,
          bancosRes,
          telefonosTiposRes,
        ] = results;

        setReferenceData({
          tiposDoc: getValue(tiposDocRes),
          provincias: getValue(provinciasRes),
          sectores: getValue(sectoresRes),
          puestos: getValue(puestosRes),
          sucursales: getValue(sucursalesRes),
          estadosCiviles: getValue(estadosCivilesRes),
          paises: getValue(paisesRes),
          sindicatos: getValue(sindicatosRes),
          bancos: getValue(bancosRes),
          telefonosTipos: getValue(telefonosTiposRes),
          isLoaded: true,
        });
      } catch (error) {
        console.error("Error loading reference data in context:", error);
      }
    };

    loadData();
  }, []);

  return { referenceData };
};

export const LegajoTabsContext = createContext();

const getInitialState = () => ({
  datosPersonales: {
    cuil: null,
    nro_doc: null,
    nro_legajo: null,
    nombre: null,
    apellido: null,
    email: null,
    fecha_ingreso: null,
    fecha_baja: null,
    fecha_nacimiento: null,
    id_tipo_doc: null,
    id_pais: 1, // Default to Argentina if appropriate
    id_provincia: null,
    id_localidad: null,
    domicilio: null,
    sexo: null,
    patente_automovil: null,
    id_estado_civil: null,
    latitud: null,
    longitud: null,
    activo: true,
    xdistancia: 1,
    username: localStorage.getItem("user"),
    uuid_foto: null, // Vacío inicialmente


  },
  familiares: [],
  datosLaborales: {
    empleado_cuil: null,
    sucursal_id: null,
    puesto_id: null,
    sector_id: null,
    controlhorario: false,
    categoria_id: null,
    convenio_id: null,
    gremial_id: null,
    sindicato_id: null,
    tarea_id: null,
    tipo_contrato: null,
    presentismo: false,
    paga_sindical: false,
    liqui_salario_familiar: false,
    fuera_convenio: false,
    observaciones: null,
    obra_social_id: null,
    nro_afiliado_os: null,
    banco_id: null,
    alias: null,
    cbu: null,
    nro_cuenta: null,
    liquida_ganancias: false,
    regimen_id: null,
    uuid_archivo: null,
    antiguedad_anterior: null,
    chofer_nro: null,
  },

  telefonosEmpleado: [],
});

export const LegajoTabsProvider = ({ children }) => {
  const [formData, setFormData] = useState(() => getInitialState());
  const [errors, setErrors] = useState({});
  const { referenceData } = useLegajoReferenceData();

  // Reset form to initial state
  const resetForm = React.useCallback(() => {
    setFormData(getInitialState());
    setErrors({});
  }, []);

  // Función para generar UUID solo cuando se necesite
  const ensureUuidFoto = React.useCallback(() => {
    if (!formData.datosPersonales.uuid_foto) {
      const newUuid = uuidv4();
      setFormData((prevData) => ({
        ...prevData,
        datosPersonales: {
          ...prevData.datosPersonales,
          uuid_foto: newUuid,
        },
      }));
      return newUuid;
    }
    return formData.datosPersonales.uuid_foto;
  }, [formData.datosPersonales.uuid_foto]);

  // Función para generar UUID de archivo laboral cuando se necesite
  const ensureUuidArchivo = React.useCallback(() => {
    if (!formData.datosLaborales.uuid_archivo) {
      const newUuid = uuidv4();
      setFormData((prevData) => ({
        ...prevData,
        datosLaborales: {
          ...prevData.datosLaborales,
          uuid_archivo: newUuid,
        },
      }));
      return newUuid;
    }
    return formData.datosLaborales.uuid_archivo;
  }, [formData.datosLaborales.uuid_archivo]);

  // Funciones específicas para cada tipo de dato
  const updateFormData = React.useCallback((tabName, eventOrData) => {
    // Solo para objetos (datosPersonales, datosLaborales, etc.)
    if (eventOrData && eventOrData.target) {
      const { name, value, type, checked } = eventOrData.target;
      const finalValue = type === "checkbox" ? checked : value;

      setFormData((prevData) => ({
        ...prevData,
        [tabName]: {
          ...prevData[tabName],
          [name]: finalValue,
        },
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [tabName]: {
          ...prevData[tabName],
          ...eventOrData,
        },
      }));
    }
  }, []);

  // Handlers específicos para lógica de negocio
  const handlePersonalDataChange = React.useCallback((e) => {
    updateFormData("datosPersonales", e);

    const { name, value } = e.target;

    if (name === "fecha_baja" && value) {
      setFormData(prev => ({
        ...prev,
        datosPersonales: { ...prev.datosPersonales, activo: false }
      }));
    }
    if (name === "fecha_ingreso" && value) {
      setFormData(prev => {
        // Si tenía fecha de baja previa, reactivamos y limpiamos fecha de baja
        if (prev.datosPersonales.fecha_baja) {
          return {
            ...prev,
            datosPersonales: { ...prev.datosPersonales, activo: true, fecha_baja: null }
          };
        }
        return prev;
      });
    }
  }, [updateFormData]);


  const setArrayData = React.useCallback((array, tabName) => {
    setFormData((prev) => ({
      ...prev,
      [tabName]: Array.isArray(array) ? array : [],
    }));
  }, []);

  const removeFamilyMember = React.useCallback((index) => {
    setFormData((prevData) => ({
      ...prevData,
      familiares: Array.isArray(prevData.familiares)
        ? prevData.familiares.filter((_, i) => i !== index)
        : [],
    }));
  }, []);

  const addFamilyMember = React.useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      familiares: [
        ...(Array.isArray(prev.familiares) ? prev.familiares : []),
        {
          nombre: "",
          apellido: "",
          id_tipo_doc: "",
          nro_doc: "",
          parentesco_id: "",
          telefono: "",
          domicilio: "",
          fecha_nacimiento: null,
          empleado_cuil: prev.datosPersonales?.cuil || "",
          username: localStorage.getItem("user"),
        },
      ],
    }));
  }, []);

  const updateFamilyMember = React.useCallback((index, field, value) => {
    setFormData((prev) => {
      const newFamily = [...(Array.isArray(prev.familiares) ? prev.familiares : [])];
      if (newFamily[index]) {
        newFamily[index] = { ...newFamily[index], [field]: value };
      }
      return { ...prev, familiares: newFamily };
    });
  }, []);

  // --- Handlers para Telefonos ---
  const addPhone = React.useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      telefonosEmpleado: [
        ...(Array.isArray(prev.telefonosEmpleado) ? prev.telefonosEmpleado : []),
        {
          tipo: "",
          numero: "",
          empleado_cuil: prev.datosPersonales?.cuil,
          username: localStorage.getItem("user")
        },
      ],
    }));
  }, []);

  const removePhone = React.useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      telefonosEmpleado: (Array.isArray(prev.telefonosEmpleado) ? prev.telefonosEmpleado : []).filter((_, i) => i !== index),
    }));
  }, []);

  const updatePhone = React.useCallback((index, field, value) => {
    setFormData((prev) => {
      const newPhones = [...(Array.isArray(prev.telefonosEmpleado) ? prev.telefonosEmpleado : [])];
      if (newPhones[index]) {
        newPhones[index] = { ...newPhones[index], [field]: value };
      }
      return { ...prev, telefonosEmpleado: newPhones };
    });
  }, []);

  const contextValue = React.useMemo(
    () => ({
      formData,
      updateFormData,
      handlePersonalDataChange,
      setArrayData,
      removeFamilyMember,
      addFamilyMember,
      updateFamilyMember,
      addPhone,
      removePhone,
      updatePhone,
      ensureUuidFoto,
      ensureUuidArchivo,
      errors,
      setErrors,
      referenceData,
      resetForm, // Exposed reset function
    }),
    [
      formData,
      updateFormData,
      handlePersonalDataChange,
      setArrayData,
      removeFamilyMember,
      addFamilyMember,
      updateFamilyMember,
      addPhone,
      removePhone,
      updatePhone,
      ensureUuidFoto,
      ensureUuidArchivo,
      errors,
      referenceData,
      resetForm,
    ]
  );
  return <LegajoTabsContext.Provider value={contextValue}>{children}</LegajoTabsContext.Provider>;
};

// Compartir el context
export const useLegajosContext = () => useContext(LegajoTabsContext);
